import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
} from "@angular/core";
import { AngularEditorConfig } from "@kolkov/angular-editor";
import { NgbActiveModal, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { forkJoin, Observable, ObservableInput, Subject } from "rxjs";
import { ConfirmComponent } from "src/app/components/common/confirm/confirm.component";
import {
  INews,
  IParts,
  NewsEditModalLabels,
  PartImage,
  PartType,
  NewsOperationType,
} from "src/app/models/news.model";
import { UserModel } from "src/app/models/user.model";
import { NewsService } from "src/app/services/news.service";
import { BootstrapAlert } from "src/app/widgets/ng-bootstrap-alert/bootstrap-alert";
import { BootstrapAlertService } from "src/app/widgets/ng-bootstrap-alert/bootstrap-alert.service";
import { takeUntil } from "rxjs/operators";
import { FileService } from "src/app/services/file.service";
import { AttachmentModel, UploadType } from "src/app/models/document.model";
import {
  GET_NEWS_SUCCESS,
  UPDATE_NEWS_SUCCESS,
} from "src/app/store/news/actions";
import { Actions, ofType } from "@ngrx/effects";

@Component({
  selector: "app-news-edit-modal",
  templateUrl: "./news-edit-modal.component.html",
  styleUrls: ["./news-edit-modal.component.scss"],
})
export class NewsEditModalComponent implements OnInit, OnDestroy {
  data: {
    newsItem: INews;
    user: UserModel;
    type: NewsOperationType;
  };
  public files: Set<File> = new Set();
  progress;
  attachmentsProgress: {
    [key: string]: { progress: Observable<number>; response: Observable<any> };
  }[] = [];
  isError = false;
  errorMessage: string;
  uploading = false;
  clock_tick: number;
  fileMessage: string = "";
  previewSrc: string | undefined;
  thumbnailImage: PartImage = null;
  markdownContent: string = "";
  @Output() finishNewsEdit: EventEmitter<Set<File>> = new EventEmitter<
    Set<File>
  >();
  uploadAttachmentsComplete: EventEmitter<AttachmentModel[]> = new EventEmitter<
    AttachmentModel[]
  >();
  config: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: "auto",
    minHeight: "120px",
    maxHeight: "500px",
    width: "auto",
    minWidth: "0",
    translate: "now",
    enableToolbar: true,
    showToolbar: true,
    placeholder: "",
    defaultParagraphSeparator: "",
    defaultFontName: "",
    defaultFontSize: "4",
    uploadUrl: undefined,
    fonts: [
      { class: "arial", name: "Arial" },
      { class: "times-new-roman", name: "Times New Roman" },
      { class: "calibri", name: "Calibri" },
      { class: "comic-sans-ms", name: "Comic Sans MS" },
    ],
    sanitize: true,
    toolbarPosition: "top",
    toolbarHiddenButtons: [
      [
        "undo",
        "redo",
        "strikeThrough",
        "subscript",
        "superscript",
        "insertImage",
        "insertVideo",
        "insertHorizontalRule",
        "toggleEditorMode",
        "heading",
        "fontName",
        "textColor",
        "backgroundColor",
      ],
      ["fontSize"],
    ],
  };

  private unsubscribe: Subject<boolean> = new Subject();
  labels: NewsEditModalLabels;
  disableButtons = false;

  // Attachments
  attachments: Set<File> = new Set();
  attachmentsAlreadyUploaded: AttachmentModel[] = [];
  attachmentsResponses: AttachmentModel[] = [];
  attachmentsToDelete: string[] = [];

  isEditMode: boolean = false;
  newsId!: string;

  constructor(
    public activeModal: NgbActiveModal,
    private readonly newsService: NewsService,
    private readonly fileService: FileService,
    private readonly bootstrapAlertService: BootstrapAlertService,
    private readonly modalService: NgbModal,
    protected translate: TranslateService,
    private readonly actions$: Actions,
  ) {
    // Translations from assets/global/i18n/en.json
    this.translate
      .get(["newsEditModal"])
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this.labels = value["newsEditModal"];
      });

    this.onUploadAttachmentsComplete();
    this.onUpdateNewsSuccess();
  }

  async ngOnInit() {
    this.errorMessage = "";
    this.clock_tick = Date.now();
    this.onGetNewsSuccess();

    this.setContent();
  }

  async setContent() {
    this.data = {
      ...this.data,
      newsItem: JSON.parse(JSON.stringify(this.data.newsItem)),
    };

    this.newsId = this.data.newsItem.id;

    this.setThumbnailImage();

    this.markdownContent = await this.newsService.parseMarkdownContent(
      this.data.newsItem.parts,
    );

    const initialAttachments = this.newsService.getAttachments(
      this.data.newsItem.parts,
    );
    this.attachmentsResponses = initialAttachments || [];
    this.attachmentsAlreadyUploaded = [...this.attachmentsResponses];
    this.setIsEditMode();
  }

  setIsEditMode() {
    this.isEditMode = this.data.type === NewsOperationType.Put ? true : false;
  }

  // ATTACHMENTS MANAGEMENT
  // Upload
  onAttachmentFilesAdded(files: Set<File>) {
    this.attachmentsProgress = [];
    this.isError = false;
    this.disableButtons = true;

    for (const key in files) {
      if (!Number.isNaN(Number.parseInt(key, 10))) {
        this.attachments.add(files[key]);
      }
    }
    const attachmentUploadProgress = this.prepareAttachmentUploadProgress();

    if (!attachmentUploadProgress.length) {
      return;
    }

    this.handleAttachmentUploadCompletion(attachmentUploadProgress);
  }

  private prepareAttachmentUploadProgress(): ObservableInput<any>[] {
    const attachmentUploadProgress: ObservableInput<any>[] = [];

    // Filter out already uploaded attachments to avoid re-uploading them
    const newAttachments = Array.from(this.attachments).filter(
      (attachment) =>
        !this.attachmentsAlreadyUploaded.some(
          (uploaded) => uploaded.title === attachment.name,
        ),
    );

    // Upload files and collect progress observables
    for (const file of newAttachments) {
      const uploadProgress = this.fileService.upload(
        new Set([file]),
        UploadType.Attachment,
        file.name,
        new Date().toISOString(),
        this.data.user,
      );

      this.attachmentsProgress.push(uploadProgress);
    }

    // Build progress array and subscribe to responses
    for (const uploadProgress of this.attachmentsProgress) {
      for (const key in uploadProgress) {
        if (uploadProgress.hasOwnProperty(key)) {
          attachmentUploadProgress.push(uploadProgress[key].progress);

          uploadProgress[key].response
            .pipe(takeUntil(this.unsubscribe))
            .subscribe({
              next: (body: AttachmentModel) => {
                this.addAttachmentIfNotExists(body, this.attachmentsResponses);
                this.addAttachmentIfNotExists(
                  body,
                  this.attachmentsAlreadyUploaded,
                );
              },
              error: (error) => {
                this.handleError(error);
              },
            });
        }
      }
    }

    return attachmentUploadProgress;
  }

  private addAttachmentIfNotExists(
    attachment: AttachmentModel,
    target: AttachmentModel[],
  ): void {
    const alreadyExists = target.some(
      (existing) =>
        existing.id === attachment.id || existing.title === attachment.title,
    );
    if (!alreadyExists) {
      target.push(attachment);
    }
  }

  private handleAttachmentUploadCompletion(
    attachmentUploadProgress: ObservableInput<any>[],
  ): void {
    forkJoin(attachmentUploadProgress).subscribe({
      next: () => {
        this.uploadAttachmentsComplete.emit(this.attachmentsResponses);
        this.attachmentsProgress = [];
      },
      error: (error) => {
        this.handleError(error);
        this.attachmentsProgress = [];
      },
    });
  }

  onUploadAttachmentsComplete() {
    this.uploadAttachmentsComplete.subscribe(
      (_responses: AttachmentModel[]) => {
        this.disableButtons = false;
        this.updateIfPutOperation();
      },
    );
  }

  // Delete
  onAttachmentDeleted(id: string): void {
    this.handleAttachmentDeletion(id);
  }

  onAttachmentDeleteFailed(error: any): void {
    this.handleError(error);
  }

  private handleAttachmentDeletion(id: string) {
    this.attachments.delete(
      Array.from(this.attachments).find(
        (att) =>
          att.name ===
          this.attachmentsResponses.find((ar) => ar.id === id)?.title,
      ),
    );
    this.attachmentsResponses = this.attachmentsResponses.filter(
      (f) => f.id !== id,
    );
    this.attachmentsAlreadyUploaded = this.attachmentsAlreadyUploaded.filter(
      (f) => f.id !== id,
    );
    this.attachmentsToDelete.push(id);
    this.updateIfPutOperation();
  }

  private updateIfPutOperation(): void {
    if (this.isEditMode) {
      this.updateData(false, false);
    }
  }

  private removeAttachmentsFromNewsParts() {
    if (!this.attachmentsToDelete.length) {
      return;
    }
    this.data.newsItem.parts = this.data.newsItem.parts.filter((part) => {
      if (part.type === PartType.Document) {
        return !this.attachmentsToDelete.includes(part.document.id);
      }
      return true;
    });
  }

  // END ATTACHMENTS MANAGEMENT

  // THUMBNAIL MANAGEMENT
  setThumbnailImage() {
    this.thumbnailImage = this.newsService.getThumbnailImage(
      this.data.newsItem.parts,
    );
  }

  clearFileInput(event: Event) {
    const input = event.target as HTMLInputElement;
    input.value = "";
  }

  onThumbnailAdded(event: Event) {
    this.thumbnailImage = null;
    this.previewSrc = undefined;
    this.isError = false;
    const input = event.target as HTMLInputElement;
    const files = input.files;

    for (const key in files) {
      if (!Number.isNaN(Number.parseInt(key, 10))) {
        this.files.clear();
        this.progress = null;
        this.files.add(files[key]);
      }
    }
    if (this.files.size) {
      this.fileMessage = Array.from(this.files)
        .map((f) => f.name)
        .join(", ");

      const reader = new FileReader();
      reader.onload = () => (this.previewSrc = reader.result as string);
      reader.readAsDataURL(Array.from(this.files)[0]);
      this.updateIfPutOperation();
    }
  }

  deleteThumbnail(event: Event) {
    event.stopPropagation();
    event.preventDefault();

    if (!this.isEditMode) {
      this.clearThumbnailImage();
      return;
    }

    if (!this.thumbnailImage) {
      return;
    }

    this.newsService
      .deleteThumbnail(this.thumbnailImage.id, this.data.newsItem.entityId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: () => {
          this.clearThumbnailImage();
          this.updateIfPutOperation();
        },
        error: (error) => {
          this.handleError(error);
        },
      });
  }

  clearThumbnailImage() {
    this.files.clear();
    this.fileMessage = "";
    this.previewSrc = undefined;
    this.thumbnailImage = null;
    this.progress = null;
  }
  // END THUMBNAIL MANAGEMENT

  confirmPublish(publish: boolean) {
    const modalRef = this.modalService.open(ConfirmComponent, {
      centered: true,
    });
    const instance = modalRef.componentInstance as ConfirmComponent;

    instance.message = !publish
      ? "By unpublishing the news, all users will lose access to the news item; are you sure you want to continue?"
      : "By publishing the news, all users identified using the Application, Role, and Competition criteria will receive a notification; are you sure you want to continue?";
    instance.confirmText = publish ? "Publish" : "Unpublish";
    instance.cancelText = "Cancel";

    const s = instance.confirm.subscribe((_) => {
      this.updateData(publish);
      s.unsubscribe();
    });
  }

  updateData(publish?: boolean, closedModal: boolean = true) {
    this.uploading = true;
    this.disableButtons = true;
    this.removeAttachmentsFromNewsParts();
    const structuredNewsData = this.getNewsData(publish);

    this.progress = this.newsService.createOrUpdate(
      this.files,
      structuredNewsData,
      this.data.type,
    );

    const allProgressObservables: ObservableInput<any>[] = [];
    for (const key in this.progress) {
      if (this.progress.hasOwnProperty(key)) {
        allProgressObservables.push(this.progress[key].progress);
      }
    }

    // When all progress-observables are completed...
    forkJoin(allProgressObservables).subscribe({
      next: (value: any) => {
        this.onFinishUpate(undefined, closedModal);
      },
      error: (error) => {
        this.onFinishUpate(error, closedModal);
      },
    });
  }

  onFinishUpate(error?: any, closedModal: boolean = true) {
    this.uploading = false;
    this.disableButtons = false;
    this.progress = null;
    if (error) {
      this.handleError(error);
    } else {
      this.finishNewsEdit.emit(this.files);
    }
    if (closedModal) {
      this.activeModal.close();
    }
  }

  onGetNewsSuccess(): void {
    this.actions$
      .pipe(ofType(GET_NEWS_SUCCESS), takeUntil(this.unsubscribe))
      .subscribe((data: any) => {
        const thumbnailPart = data.payload.items
          .find((news: INews) => news.id === this.newsId)
          ?.parts.find((part) => part.type === PartType.Photo);

        if (thumbnailPart) {
          this.thumbnailImage = thumbnailPart.image;
        }

        this.disableButtons = false;
        this.uploading = false;
      });
  }

  onUpdateNewsSuccess(): void {
    this.actions$
      .pipe(ofType(UPDATE_NEWS_SUCCESS), takeUntil(this.unsubscribe))
      .subscribe((data: any) => {
        this.newsId = data.payload.id;
      });
  }

  getNewsData(publish?: boolean): INews {
    this.data.newsItem = {
      ...this.data.newsItem,
      id: this.newsId,
      ...(this.data.type === NewsOperationType.Post
        ? {
            createdBy: this.getUserName(),
            createdById: this.data.user.email,
            contentDate: new Date().toISOString(),
            isUrgent: false,
          }
        : {}),
      published: publish != null ? publish : this.data.newsItem.published,
      lastUpdatedBy: this.getUserName(),
      lastUpdatedById: this.data.user.email,
      lastUpdatedDate: new Date().toISOString(),
      parts: this.getPartsData(),
    };
    return this.data.newsItem;
  }

  getUserName(): string {
    const firstName = !!this.data.user.firstName
      ? this.data.user.firstName
      : "";
    const lastName = !!this.data.user.lastName ? this.data.user.lastName : "";
    return `${firstName} ${lastName}`.trim();
  }

  getPartsData(): IParts[] {
    const parts: IParts[] = [];
    parts.push({
      type: PartType.Markdown,
      content: this.markdownContent,
    });

    if (this.thumbnailImage) {
      parts.push({
        type: PartType.Photo,
        content: "",
        image: {
          id: this.thumbnailImage.id,
          downloadUrl: this.thumbnailImage.downloadUrl,
          previewUrl: this.thumbnailImage.previewUrl,
          thumbnailUrl: this.thumbnailImage.thumbnailUrl,
          title: this.thumbnailImage.title,
        },
      });
    }

    if (this.attachmentsResponses.length > 0) {
      this.attachmentsResponses.forEach((attachment) => {
        parts.push({
          type: PartType.Document,
          document: {
            title: attachment.title,
            id: attachment.id,
            downloadUrl: attachment.downloadUrl,
          },
        });
      });
    }

    return parts;
  }

  close() {
    this.activeModal.close();
  }

  private handleError(error: any): void {
    this.bootstrapAlertService.alert(
      new BootstrapAlert("Error", "alert-danger"),
    );
    this.isError = true;
    this.errorMessage = `${error.message} ${error.error?.ExceptionMessage}`;
  }

  ngOnDestroy(): void {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }
}
